<template>
    <page-wrapper>
        <page-header :progress-bar="isQueuedOrProcessing" progress-bar-indeterminate>
            <template v-slot:breadcrumbs>
                <q-breadcrumbs-el :label="$tc('common.accounting.account-balance-report.account-balance-report', 2)" :to="{ name: 'accounting-account-balance-reports' }" />
            </template>

            <h1>{{ $tc('common.accounting.account-balance-report.account-balance-report', 2) }} <info-icon :text="$t('views.accounting.account-balance-report.account-balance-report--info-text')" size="0.6em" /></h1>
        </page-header>

        <div class="row">
            <div class="col-12">
                <base-table
                    ref="table"
                    :columns="columns"
                    :fetch-objects-fn="AccountBalanceReport.objects.all"
                    user-settings-base-path="AccountBalanceReportList"
                    pagination-sort-by-default-key="created_at"
                    pagination-initial-order-direction-descending
                    selection="multiple"
                    :selected.sync="selectedItems"
                    :selection-row-condition="selectionRowCondition"
                    :show-filter="false"
                    enable-visible-columns
                    @row-dblclick="(evt, row) => $router.push({ name: 'accounting-account-balance-report-detail', params: { id: row.id } })"
                >
                    <template v-slot:selectionRowConditionInfo>
                        <q-checkbox
                            :value="false"
                            class="not-selectable"
                            color="secondary-light"
                            keep-color
                            dense
                            disable
                        />
                    </template>

                    <template v-slot:header-cell-owner="slotProps">
                        <q-th :props="slotProps">
                            {{ $tc('views.accounting.account-balance-report.column-title--created-by', 1) }}
                            <info-icon :text="$t('views.accounting.account-balance-report.created-by--info-text')" />
                        </q-th>
                    </template>

                    <template v-slot:body-cell-accounts="slotProps">
                        <q-td :props="slotProps">
                            <template v-if="slotProps.row.formattedAccountList.teaser">
                                {{ slotProps.row.formattedAccountList.teaser.full }}
                                <info-icon :text="slotProps.row.formattedAccountList.accounts" />
                            </template>
                            <template v-else>{{ slotProps.row.formattedAccountList.accounts }}</template>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-status="slotProps">
                        <q-td :props="slotProps" auto-width>
                            <!-- TODO @TFU: Provide list with all `currentQueueItem.status_message.code` and their meaning. -->
                            <!-- TODO @MTR: Translations for error messages. -->
                            <status-badge
                                :status="slotProps.row.currentQueueItem.status"
                                default-translation-base-path="common.status.queue-item-status"
                                :loading="[QueueItemStatus.QUEUED, QueueItemStatus.PROCESSING].includes(slotProps.row.currentQueueItem.status)"
                                :info-text="slotProps.row.currentQueueItem.status === QueueItemStatus.FAILED ? slotProps.row.currentQueueItem.status_message.message : null"
                            />
                        </q-td>
                    </template>

                    <template v-slot:body-cell-actions="slotProps">
                        <q-td :props="slotProps" auto-width>
                            <q-btn
                                color="primary"
                                size="sm"
                                flat
                                round
                                icon="mib-view-1"
                                :to="{ name: 'accounting-account-balance-report-detail', params: { id: slotProps.row.id } }"
                            >
                                <q-tooltip :delay="1000" :offset="[0, 10]">{{ $tc('common.term.show-details', 1) }}</q-tooltip>
                            </q-btn>
                        </q-td>
                    </template>
                </base-table>

                <!-- Cancel Creation Account Balance Report -->
                <form-dialog
                    ref="cancelCreationDialog"
                    dont-close-on-confirm
                    @confirm="handleCancelCreation"
                >
                    <template v-slot:title>{{ $tc('common.actions.accounting.account-balance-report.cancel-creation', eligibleItemsForCancelCreation.length) }}</template>
                    <template v-slot:default>
                        <info-box v-if="eligibleItemsForCancelCreation.length !== selectedItems.length">{{ $tc('views.accounting.account-balance-report.cancel-report-creation--confirm--n-of-x--info', eligibleItemsForCancelCreation.length, { x: selectedItems.length }) }}</info-box>
                        <p>{{ $tc(`views.accounting.account-balance-report.cancel-report-creation--confirm${eligibleItemsForCancelCreation.length !== selectedItems.length ? '--n-of-x' : '' }`, eligibleItemsForCancelCreation.length) }}</p>
                    </template>
                </form-dialog>

                <!-- Delete Account Balance Report -->
                <form-dialog
                    ref="deleteDialog"
                    double-confirm
                    dont-close-on-confirm
                    @confirm="handleDelete"
                >
                    <template v-slot:title>{{ $tc('common.accounting.account-balance-report.delete-report', eligibleItemsForDelete.length) }}</template>
                    <template v-slot:default>
                        <info-box v-if="eligibleItemsForDelete.length !== selectedItems.length">{{ $tc('views.accounting.account-balance-report.delete-report--confirm--n-of-x--info', eligibleItemsForDelete.length, { x: selectedItems.length }) }}</info-box>
                        <p>{{ $tc(`views.accounting.account-balance-report.delete-report--confirm${eligibleItemsForDelete.length !== selectedItems.length ? '--n-of-x' : '' }`, eligibleItemsForDelete.length) }}</p>
                    </template>
                </form-dialog>
            </div>
        </div>

        <polling
            ref="polling"
            :poll-fn="AccountBalanceReport.service.all"
            :stop-condition-fn="stopPollingConditionFn"
            :poll-filters="pollFilters"
            :timeout-tm="3000"
            auto-start
            @condition-check="handlePollingConditionCheck"
        />

        <in-page-footer>
            <base-button
                :to="{ name: 'accounting-account-balance-report-create' }"
                :label="$tc('common.accounting.account-balance-report.create-report', 1)"
                primary-button
            />

            <base-button
                v-if="eligibleItemsForCancelCreation.length"
                :label="`${$tc('common.accounting.account-balance-report.cancel-creation', eligibleItemsForCancelCreation.length)} (${eligibleItemsForCancelCreation.length})`"
                primary-button
                outline
                @click="handleActions({ key: AccountBalanceReport.action.CANCEL_CREATION })"
            />

            <base-button
                v-if="eligibleItemsForDelete.length"
                :label="`${$tc('common.actions.accounting.account-balance-report.delete', eligibleItemsForDelete.length)} (${eligibleItemsForDelete.length})`"
                outline
                color="negative"
                @click="handleActions({ key: AccountBalanceReport.action.DELETE })"
            />
        </in-page-footer>
    </page-wrapper>
</template>

<script>
import { kebabCase } from 'lodash'
import { EventBus } from '@/event-bus'
import { AccountBalanceReportActions } from '@/enums'
import { QueueItemStatus } from '@/enums/graphql'
import { AccountBalanceReport } from '@/models/models'
import { cancelCreationCondition, deleteCondition } from '@/helpers/accountBalanceReport'

import BaseTable from '@/components/BaseTable'
import StatusBadge from '@/components/StatusBadge'
import FormDialog from '@/components/form/FormDialog'
import Polling from '@/components/Polling.vue'

export default {
    name: 'AccountBalanceReportList',
    meta () {
        return {
            title: this.$tc('common.accounting.account-balance-report.account-balance-report', 2),
        }
    },
    components: {
        BaseTable,
        StatusBadge,
        FormDialog,
        Polling,
    },
    data () {
        return {
            QueueItemStatus,
            AccountBalanceReport,
            AccountBalanceReportActions,
            selectedItems: [],
            columns: [
                {
                    name: 'date_from',
                    label: this.$tc('views.accounting.account-balance-report.column-title--period-from', 1),
                    align: 'left',
                    sortable: true,
                    field: row => row.formattedDateFrom,
                    required: true,
                },
                {
                    name: 'date_until',
                    label: this.$tc('views.accounting.account-balance-report.column-title--period-to', 1),
                    align: 'left',
                    sortable: true,
                    field: row => row.formattedDateUntil,
                    required: true,
                },
                {
                    name: 'accounts',
                    label: this.$tc('views.accounting.account-balance-report.column-title--accounts', 2),
                    align: 'left',
                    required: true,
                },
                {
                    name: 'created_at',
                    label: this.$tc('common.term.created-on', 1),
                    align: 'left',
                    sortable: true,
                    field: row => row.formattedCreatedAt,
                },
                {
                    name: 'owner',
                    label: this.$tc('views.accounting.account-balance-report.column-title--created-by', 1),
                    align: 'left',
                    sortable: true,
                    field: row => row.owner.getContactName(),
                },
                {
                    name: 'file_types',
                    label: this.$tc('common.file.file-type', 1),
                    align: 'left',
                    field: row => row.file_types.map(file_type => this.$tc(`common.file.type.${kebabCase(file_type)}`, 1)),
                },
                {
                    name: 'status',
                    label: this.$tc('common.term.status'),
                    align: 'left',
                    required: true,
                },
                { name: 'actions', label: this.$tc('common.term.action', 2), align: 'left', required: true },
            ],
            isQueuedOrProcessing: false,
            pollFilters: {
                count: 1,
                filterCurrentQueueItemStatus: [QueueItemStatus.QUEUED, QueueItemStatus.PROCESSING],
            },
        }
    },
    computed: {
        eligibleItemsForCancelCreation () {
            return this.getEligibleItemsForCancelCreation()
        },
        eligibleItemsForDelete () {
            return this.getEligibleItemsForDelete()
        },
    },
    created () {
        EventBus.$on('shortcut:newEntity', this.onNewEntity)
    },
    beforeDestroy () {
        EventBus.$off('shortcut:newEntity', this.onNewEntity)
    },
    methods: {
        handleActions (action) {
            switch (action.key) {
                case AccountBalanceReport.action.CANCEL_CREATION:
                    this.$refs.cancelCreationDialog.open()
                    break

                case AccountBalanceReport.action.DELETE:
                    this.$refs.deleteDialog.open()
                    break
            }
        },
        handleCancelCreation () {
            const accountBalanceReportIds = this.eligibleItemsForCancelCreation.map(accountBalanceReport => accountBalanceReport.id)

            AccountBalanceReport.cancelCreation(accountBalanceReportIds)
                .then(accountBalanceReports => {
                    this.$refs.cancelCreationDialog.close()
                    this.resetSelectedItems()
                    this.$refs.table.fetchObjects()

                    this.$q.notify({
                        type: 'positive',
                        message: this.$tc('common.notifications.accounting.account-balance-report-creation-cancelled-success', accountBalanceReports.length),
                    })
                }).catch(error => {
                    this.$refs.cancelCreationDialog.showFormErrorMessage(error)
                    this.$refs.cancelCreationDialog.resetFormSubmitStatus()
                })
        },
        handleDelete () {
            const accountBalanceReportIds = this.eligibleItemsForDelete.map(accountBalanceReport => accountBalanceReport.id)

            AccountBalanceReport.delete(accountBalanceReportIds)
                .then(() => {
                    this.$refs.deleteDialog.close()
                    this.resetSelectedItems()
                    this.$refs.table.fetchObjects()

                    this.$q.notify({
                        type: 'positive',
                        message: this.$tc('common.notifications.accounting.account-balance-report-deleted-success', accountBalanceReportIds.length),
                    })
                }).catch(error => {
                    this.$refs.deleteDialog.showFormErrorMessage(error)
                    this.$refs.deleteDialog.resetFormSubmitStatus()
                })
        },
        selectionRowCondition () {
            return true
        },
        resetSelectedItems () {
            this.selectedItems.length = 0
            if (this.$refs.table) this.$refs.table.$refs.qTable.clearSelection()
        },
        getEligibleItemsForCancelCreation () {
            return cancelCreationCondition(this.selectedItems)
        },
        getEligibleItemsForDelete () {
            return deleteCondition(this.selectedItems)
        },
        stopPollingConditionFn (response) {
            return !response.data.length
        },
        handlePollingConditionCheck (stopCondition) {
            if (this.isQueuedOrProcessing && stopCondition) this.$refs.table.fetchObjects()
            this.isQueuedOrProcessing = !stopCondition
        },
        onNewEntity () {
            this.$router.push({ name: 'accounting-account-balance-report-create' })
        },
    },
}
</script>
